import { AsyncPipe, Location, NgClass, NgIf } from '@angular/common';
import {
  Component,
  computed,
  effect,
  EventEmitter,
  HostListener,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogConfig,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatHint } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import cronstrue from 'cronstrue';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { difference, find, get, isEqual, pick } from 'lodash';
import { LocaleService } from 'ngx-daterangepicker-material';
import { Editor, toHTML, Toolbar } from 'ngx-editor';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  merge,
  Observable,
  shareReplay,
  startWith,
  Subject,
  Subscription,
  switchMap,
  take,
} from 'rxjs';
import { map } from 'rxjs/operators';
import { AdvancedFormGroup } from '../../../core/classes/advanced-form-group.class';
import { DynamicObservable } from '../../../core/classes/dynamic-observable';
import { AdaptiveMatDialogRef } from '../../../core/classes/mat-dialog-ref-injectables';
import { IS_HEADER_HIDDEN } from '../../../core/constants/embed.constants';
import { EDialogWidth } from '../../../core/constants/ui.constants';
import { TrackFocusedOnceDirective } from '../../../core/directives/track-focused-once.directive';
import { TypeFromObs } from '../../../core/interfaces/utility-types.interface';
import { RelativeTimePipe } from '../../../core/pipes/relative-time.pipe';
import { UtcToUserFormattedPipe } from '../../../core/pipes/utc-to-user-formatted.pipe';
import { NavigationInterventionService } from '../../../core/services/navigation-intervention.service';
import { TextEditorRequiredValidator } from '../../../core/validators/text-editor-required.validator';
import { TrimmedRequiredValidator } from '../../../core/validators/trimmed-required.validator';
import {
  ConfirmationDialogComponent,
  IConfirmationDialogConfig,
} from '../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { DateRangePickerComponent } from '../../../shared/components/date-range-picker/date-range-picker.component';
import { IDateRangePickerDate } from '../../../shared/components/date-range-picker/date-range-picker.interface';
import { DeviceCameraModalComponent } from '../../../shared/components/device-camera-modal/device-camera-modal.component';
import { DueDateChipComponent } from '../../../shared/components/due-date-chip/due-date-chip.component';
import { EItemPriority } from '../../../shared/components/item-priority/item-priority.interface';
import { ItemPriorityService } from '../../../shared/components/item-priority/item-priority.service';
import { IDropdownOption, MatDropdown } from '../../../shared/components/mat-dropdown/mat-dropdown.component';
import { ReadToWriteInputComponent } from '../../../shared/components/read-to-write/read-to-write-input/read-to-write-input.component';
import { TextEditorComponent } from '../../../shared/components/text-editor/text-editor.component';
import { IOeeCurrentShift, IOeeShift } from '../../../shared/services/oee-tracker/oee-tracker.interface';
import { OeeTrackerStore } from '../../../shared/services/oee-tracker/oee-tracker.store';
import { ELoadStatus } from '../../../state/state.interface';
import { UserEffects } from '../../../state/user/user.effects';
import { ICurrentUser } from '../../../state/user/user.interface';
import { API_DATE_FORMAT, DateUtilitiesService } from '../../../utilities/date-utilities.service';
import { DropdownUtilitiesService } from '../../../utilities/dropdown-utilities.service';
import { FormUtilitiesService } from '../../../utilities/form-utilities.service';
import { HttpUtilitiesService } from '../../../utilities/http-utilities.service';
import { ESnackbar, SnackbarService } from '../../../utilities/snackbar.service';
import { IBoard, IBoardItemConfigurationBase } from '../../settings/boards/boards.interface';
import { EItemType, TFieldSet } from '../../settings/fields-field-sets/field-sets/field-sets.interface';
import {
  IWorkflowDiagramData,
  WorkflowDiagramDialogComponent,
} from '../../settings/workflow-management/workflows/workflow-diagram-dialog/workflow-diagram-dialog.component';
import {
  DEFAULT_WORKFLOW_NAME,
  IWorkflowStepOption,
} from '../../settings/workflow-management/workflows/workflows.interface';
import { ItemsAttachmentsComponent } from '../items-attachments/items-attachments.component';
import {
  MemoryStoredItemAttachmentFile,
  TAttachmentRules,
  TExternalSelectedFilesSignal,
} from '../items-attachments/items-attachments.interface';
import { ItemsAttachmentsStore } from '../items-attachments/items-attachments.store';
import {
  IItemBase,
  IItemJoins,
  IItemRecurrence,
  IWorkflowStepBase,
  TItemCustomFieldValue,
  TWorkflow,
  TWorkflowStepAssignment,
} from '../items.interface';
import { ItemsService } from '../items.service';
import { ApplyChangesRecursivelyQuestionModalComponent } from './apply-changes-recursively-question-modal/apply-changes-recursively-question-modal.component';
import {
  EItemRecurrenceEditType,
  ERecursiveAction,
} from './apply-changes-recursively-question-modal/apply-changes-recursively-question-modal.interface';
import { ItemCommentComponent } from './item-comment/item-comment.component';
import { ItemCustomFieldsComponent } from './item-custom-fields/item-custom-fields.component';
import { ItemCustomFieldsService } from './item-custom-fields/item-custom-fields.service';
import { ItemEscalateComponent } from './item-escalate/item-escalate.component';
import { ItemReadonlyFieldsComponent } from './item-readonly-fields/item-readonly-fields.component';
import { ItemWorkInstructionListComponent } from './item-work-instruction-list/item-work-instruction-list.component';
import { ItemsHistoryComponent } from './items-history/items-history.component';
import {
  IItemFieldsFromUrl,
  itemFieldsFromUrl,
  itemFieldsThatCanBeRecursivelyUpdated,
  itemFieldsThatCannotBeRecursivelyUpdated,
  TAddItem,
  TEditItem,
  TFormControlItem,
  TFormItem,
  TItemModalComponentData,
  TItemUpdateFields,
} from './items-modal.interface';
import { ItemsModalService } from './items-modal.service';
import {
  IItemCategoryOption,
  ItemsModalStore,
  TBoardItemConfigurationWithWorkflow,
  TFieldSetAssignment,
} from './items-modal.store';
import { LinkItemComponent } from './link-item/link-item.component';
import { IAddItemLink, IItemLinks } from './link-item/link-item.interface';
import { ItemLinkStore } from './link-item/link-item.store';
import { LinkWorkInstructionComponent } from './link-work-instruction/link-work-instruction.component';
import { RecurringItemConfigurationComponent } from './recurring-item-configuration/recurring-item-configuration.component';
import { ERecursionLimitType } from './recurring-item-configuration/recurring-item-configuration.interface';
import { RecurringItemStore } from './recurring-item.store';

dayjs.extend(utc);

@Component({
  imports: [
    MatDialogTitle,
    ReactiveFormsModule,
    MatDialogContent,
    MatButton,
    MatIconButton,
    MatMenuTrigger,
    TranslateModule,
    ItemCustomFieldsComponent,
    NgIf,
    ItemReadonlyFieldsComponent,
    LinkItemComponent,
    ItemsAttachmentsComponent,
    ItemCommentComponent,
    ItemsHistoryComponent,
    DateRangePickerComponent,
    MatDialogActions,
    MatMenu,
    MatMenuItem,
    MatIcon,
    MatHint,
    AsyncPipe,
    ReadToWriteInputComponent,
    TextEditorComponent,
    TrackFocusedOnceDirective,
    MatDropdown,
    RelativeTimePipe,
    MatTooltip,
    UtcToUserFormattedPipe,
    LinkWorkInstructionComponent,
    DueDateChipComponent,
    NgClass,
  ],
  providers: [
    ItemsModalStore,
    HttpUtilitiesService,
    LocaleService,
    ItemLinkStore,
    ItemsAttachmentsStore,
    RecurringItemStore,
    AdaptiveMatDialogRef,
    OeeTrackerStore,
  ],
  selector: 'app-items-modal',
  styleUrls: ['items-modal.component.scss'],
  templateUrl: './items-modal.component.html',
})
export class ItemsModalComponent implements OnInit, OnDestroy {
  public static readonly modalParameters: MatDialogConfig = {
    autoFocus: false,
    closeOnNavigation: false,
    disableClose: true,
    maxWidth: '100%',
    width: EDialogWidth.large,
  } as const;

  private readonly router = inject(Router);
  private readonly location = inject(Location);
  public readonly itemCategoryOptions = computed(() => {
    return this.store
      .itemCategoryOptions()
      .filter(
        (category) =>
          category.isActive || this.item?.boardItemConfiguration?.itemCategory?.id === category.itemCategoryId,
      );
  });
  public editor: Editor = new Editor({});
  public toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['code', 'blockquote'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['link', 'image'],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];
  public readonly customFieldsForm: FormGroup = new FormGroup({});
  public readonly ELoadStatus = ELoadStatus;
  private readonly useBoardOeeLineShifts = signal(false);
  private readonly boardOeeLineId = signal<number | null>(null);

  protected readonly shiftFilterActionIcon = computed((): string | undefined => {
    if (!this.boardOeeLineId()) {
      return undefined;
    }

    return this.useBoardOeeLineShifts() ? 'filter_alt_off' : 'filter_alt';
  });
  protected readonly shiftFilterActionLabelKey = computed((): string =>
    this.useBoardOeeLineShifts() ? 'button.removeLineFilter' : 'button.addLineFilter',
  );
  public readonly shiftOptions = computed((): IDropdownOption[] => {
    const allShiftOptions: IDropdownOption[] = this.store.shiftOptions();

    if (!this.useBoardOeeLineShifts()) {
      return allShiftOptions;
    }

    const lineShiftOptions: IDropdownOption[] = this.oeeTrackerStore
      .shifts()
      .map((shift) => ({ id: shift.id, name: shift.name }));
    const savedShiftId: number | null | undefined = this.item?.shiftId;

    if (!savedShiftId || lineShiftOptions.some((option) => option.id === savedShiftId)) {
      return lineShiftOptions;
    }

    // The saved shift may not be on the board's OEE line, so it comes from the unrestricted list.
    const savedShiftOption: IDropdownOption | undefined = allShiftOptions.find((option) => option.id === savedShiftId);

    return savedShiftOption ? [...lineShiftOptions, savedShiftOption] : lineShiftOptions;
  });
  public addEditForm!: AdvancedFormGroup<TFormControlItem & { customFields: FormGroup }>;
  public item?: IItemJoins;
  public itemTypeOptions: IDropdownOption<EItemType>[] = DropdownUtilitiesService.getItemTypeOptions(this.translate);
  public priorityOptions: IDropdownOption<EItemPriority>[] = this.itemPriorityService.getOptions();
  public selectedBoardId?: number;
  public hasWorkInstructions = false;
  public workflowStepOptions: IDropdownOption[] = [];
  public currentWorkflow: TWorkflow | null = null;
  public dueDate?: IDateRangePickerDate;
  public fieldSetAssignments: TFieldSetAssignment[] = [];
  public pendingLinkedItems: IItemLinks[] = [...(this.data?.linkedItems ?? [])];
  public isDefaultFieldSetSelected = true;
  public selectedFileChangeEvent: TExternalSelectedFilesSignal | undefined;
  public capturedPhotoFile: File | undefined;
  public attachmentRules!: TAttachmentRules;
  public isDeviceCameraAvailable = false;
  public isMaxFilesCountExceeded = false;
  public initialAttachments: MemoryStoredItemAttachmentFile[] = [];
  public hasAttachmentComponentOngoingProcess = true;
  public isShowComments = true;
  public userLanguage = 'en';
  public readonly attachmentLoadStatusFromComponent = new DynamicObservable<ELoadStatus>(ELoadStatus.initial);
  public readonly linkItemLoadStatusFromComponent = new DynamicObservable<ELoadStatus>(ELoadStatus.initial);
  public readonly nextDueDateDynamicObs = new DynamicObservable<string | null>(null);
  public readonly addEditFormValueChanges = new DynamicObservable<
    TypeFromObs<ItemsModalComponent['addEditForm']['valueChanges']>
  >({});
  public readonly cronToString$ = this.nextDueDateDynamicObs.obs$.pipe(
    map((cronExpression) => cronExpression && cronstrue.toString(cronExpression, { verbose: true })),
    shareReplay(1),
  );
  public readonly recurringItemDueDateHint = this.translate.instant('page.items.label.recurringItemDueDateHint');
  public readonly isRecurringUpdateAllowed$ = combineLatest({
    dataForEdit: this.store.select((state) => state.dataForEdit).pipe(filter(Boolean)),
    formValueChanges: this.addEditFormValueChanges.obs$,
  }).pipe(
    map(({ dataForEdit }) => {
      const baseWorkflowStepId = dataForEdit.itemRecurrence?.baseWorkflowStepId;
      const oldWorkflowStepId = dataForEdit.currentWorkflowStepId;
      const newWorkflowStepId = this.addEditForm.get('status')?.value?.[0]?.id;

      return Boolean(
        baseWorkflowStepId &&
          oldWorkflowStepId === newWorkflowStepId &&
          baseWorkflowStepId === newWorkflowStepId &&
          !dataForEdit.escalatedBoardId,
      );
    }),
  );

  public readonly isRecurringUpdateAllowed = toSignal(this.isRecurringUpdateAllowed$, { initialValue: false });

  private pendingAttachmentFiles: MemoryStoredItemAttachmentFile[] = [];
  private selectedRecurrenceEditType: EItemRecurrenceEditType = EItemRecurrenceEditType.onlyThis;

  private readonly subscriptions: Subscription[] = [];
  private readonly defaultItemType: IDropdownOption<EItemType> = {
    id: EItemType.action,
    name: this.translate.instant('business.action'),
  };
  public readonly isSubmitInProgress$ = combineLatest([
    this.linkItemStore.select((state) => state.crudLoading),
    this.itemsAttachmentsStore.select((state) => state.crudLoading),
    this.attachmentLoadStatusFromComponent.obs$,
    this.linkItemLoadStatusFromComponent.obs$,
    this.recurringItemStore.select((state) => state.updateLoadStatus),
    this.store.select((state) => state.addLoadStatus),
    this.store.select((state) => state.editLoadStatus),
    this.store.select((state) => state.dataForEditLoading),
  ]).pipe(
    map((statuses) => statuses.some((status) => status === ELoadStatus.loading || status === ELoadStatus.initialLoad)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private readonly destroy$ = new Subject<void>();
  public triggerWorkInstructionLoad = new EventEmitter<void>();
  public readonly isIframe = window.self !== window.top;
  public readonly isHeaderHidden: boolean = IS_HEADER_HIDDEN;
  private readonly fieldsFromUrl = this.getFieldsFromUrl();

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: TItemModalComponentData,
    public readonly store: ItemsModalStore,
    private readonly translate: TranslateService,
    public readonly dialogRef: AdaptiveMatDialogRef<boolean | undefined, ItemsModalComponent>,
    private readonly itemsService: ItemsService,
    private readonly itemsModalService: ItemsModalService,
    private readonly snackbarService: SnackbarService,
    private readonly linkItemStore: ItemLinkStore,
    private readonly itemsAttachmentsStore: ItemsAttachmentsStore,
    public readonly dialog: MatDialog,
    private readonly recurringItemStore: RecurringItemStore,
    private readonly beforeUnloadService: NavigationInterventionService,
    private readonly itemPriorityService: ItemPriorityService,
    private readonly oeeTrackerStore: OeeTrackerStore,
  ) {
    if (this.isIframeRootDialog()) {
      window.parent.postMessage(
        {
          type: 'DIALOG_OPENED',
        },
        '*',
      );
    }

    this.addEditForm = new AdvancedFormGroup({
      assignee: new FormControl([] as IDropdownOption[], {
        nonNullable: true,
        validators: [Validators.required],
      }),
      boardId: new FormControl([] as IDropdownOption[], {
        nonNullable: true,
        validators: [Validators.required],
      }),
      cronEditor: new FormGroup({
        cron: new FormControl<string | null>(null),
        endDateLimit: new FormControl<IDateRangePickerDate | null | undefined>({} as IDateRangePickerDate),
        numberLimit: new FormControl<number | null>(null),
        selectedLimitType: new FormControl<ERecursionLimitType>(ERecursionLimitType.never),
      }),
      customFields: this.customFieldsForm,
      description: new FormControl('', [Validators.required]),
      dueDate: new FormControl({} as IDateRangePickerDate, [Validators.required]),
      eventDate: new FormControl({} as IDateRangePickerDate, [Validators.required]),
      itemCategory: new FormControl([] as IItemCategoryOption[], {
        nonNullable: true,
        validators: [Validators.required],
      }),
      itemType: new FormControl([] as IDropdownOption<EItemType>[], {
        nonNullable: true,
        validators: [Validators.required],
      }),
      name: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(255)],
      }),
      priority: new FormControl([] as IDropdownOption<EItemPriority>[], [Validators.required]),
      reporter: new FormControl([] as IDropdownOption[], [Validators.required]),
      shift: new FormControl([] as IDropdownOption[], [Validators.required]),
      status: new FormControl([] as IDropdownOption[], [Validators.required]),
    });

    this.nextDueDateDynamicObs.switch(this.addEditForm.controls.cronEditor.controls.cron.valueChanges);
    this.addEditFormValueChanges.switch(this.addEditForm.valueChanges);

    if (this.data.quickItem) {
      if (this.isQuickItemFilledByUser()) {
        this.addEditForm.isFocusedOnce.next(true);
      }

      Object.entries(this.data.quickItem).forEach(([key, value]) => {
        this.addEditForm.get(key)?.reset(value);
      });
    }

    if (this.data.quickItemAttachments?.length) {
      this.initialAttachments = [...this.data.quickItemAttachments];
      this.pendingAttachmentFiles = [...this.data.quickItemAttachments];
    }

    this.beforeUnloadService.setUnloadPrevention(
      {
        shouldNavigationBeIntervened: (): boolean => this.addEditForm.isThereUnsavedChanges(),
        modalData: this.getUnloadPreventionModalData,
        isActionTakenCompleted$: this.store
          .select((state) => state.addLoadStatus)
          .pipe(filter((status) => status === ELoadStatus.success)),
        confirmationModalTruePayloadCallback: this.onFormDialogSubmit,
      },
      this.destroy$,
    );

    effect(() => {
      if (this.store.boardItemConfigurations()) {
        this.updateStatusOptions();
      }
    });

    effect(() => {
      const currentShift: IOeeCurrentShift | null = this.oeeTrackerStore.currentShift();

      if (currentShift) {
        this.addEditForm.controls.shift.reset([{ id: currentShift.shiftId, name: currentShift.shiftName }]);
      }
    });

    effect(() => {
      const lineShifts: IOeeShift[] = this.oeeTrackerStore.shifts();
      const savedShiftId: number | null | undefined = this.item?.shiftId;

      if (!savedShiftId || !lineShifts.length || lineShifts.some((shift: IOeeShift) => shift.id === savedShiftId)) {
        return;
      }

      // Fetched only now, to resolve the name of a saved shift the line list does not carry.
      this.store.loadShiftOptions({});
    });
  }

  public ngOnInit(): void {
    this.userLanguage = UserEffects.getUser().configuration.language;
    this.getDefaultFieldSet();

    if (!this.item && this.data.idForEdit) {
      this.store.getItemForEdit(this.data.idForEdit);
    }

    if (
      !this.item &&
      !this.data.idForEdit &&
      !this.data.selectedBoard &&
      !this.fieldsFromUrl.boardId &&
      !this.data.quickItem?.boardId?.length
    ) {
      this.store.getRecommendedBoardData();
    }

    if (this.data.selectedBoard) {
      this.addEditForm.get('boardId')?.reset([this.data.selectedBoard]);
    }

    this.addEditForm.get('itemType')?.reset([this.defaultItemType]);

    this.insertItemCategoryFromUrl();
    this.insertFieldsFromUrl();
    this.fillReporterFieldForAdd();
    this.searchBoards();
    this.createSubscriptions();
  }

  @HostListener('window:beforeunload', ['$event'])
  public beforeUnload($event: Event): void {
    if (!this.addEditForm.isThereUnsavedChanges()) {
      return;
    }

    $event.preventDefault();
  }

  public onFormDialogSubmit = (): void => {
    this.removedUnusedCustomFieldControls();

    if (!this.addEditForm.valid) {
      return;
    }

    const recursivelyUpdatedFields = this.getRecursivelyUpdatedFields(itemFieldsThatCanBeRecursivelyUpdated);

    if (this.isRecurringUpdateAllowed() && recursivelyUpdatedFields.length) {
      this.subscriptions.push(
        this.dialog
          .open(ApplyChangesRecursivelyQuestionModalComponent, {
            data: { mode: ERecursiveAction.edit },
          })
          .afterClosed()
          .pipe(filter(Boolean))
          .subscribe((selectedRecurrenceEditType: EItemRecurrenceEditType): void => {
            this.selectedRecurrenceEditType = selectedRecurrenceEditType;
            this.takeSubmitAction();
          }),
      );

      return;
    }

    this.takeSubmitAction();
  };

  public searchBoardMembers(search?: string, selectedUserId?: number | null): void {
    if (!this.selectedBoardId) {
      return;
    }

    this.store.loadBoardMembers({
      boardId: this.selectedBoardId,
      escalatedBoardId: this.item?.escalatedBoardId,
      search,
      selectedUserId,
    });
  }

  public assignToCurrentUser(): void {
    const currentUser: ICurrentUser = UserEffects.getUser();

    this.addEditForm.controls.assignee.setValue([{ id: currentUser.id, name: currentUser.name }]);
  }

  public isAssignToMeVisible(): boolean {
    if (!this.selectedBoardId || this.addEditForm.controls.assignee.disabled) {
      return false;
    }

    const currentUser: ICurrentUser = UserEffects.getUser();

    if (this.addEditForm.controls.assignee.value?.[0]?.id === currentUser.id) {
      return false;
    }

    const { boardIds } = currentUser.authorizations;

    if (boardIds === -1) {
      return this.store.boardMembers().some((member: IDropdownOption) => member.id === currentUser.id);
    }

    return [this.selectedBoardId, this.item?.escalatedBoardId].some(
      (boardId?: number | null) => !!boardId && boardIds.includes(boardId),
    );
  }

  public searchReporterMembers(search?: string, selectedUserId?: number | null): void {
    if (!this.selectedBoardId) {
      return;
    }

    this.store.loadReporterMembers({
      boardId: this.selectedBoardId,
      escalatedBoardId: this.item?.escalatedBoardId,
      search,
      selectedUserId,
    });
  }

  public searchBoards(search?: string): void {
    const boardId = this.addEditForm.controls.boardId.value?.[0]?.id;

    this.store.loadBoardOptions({
      fields: ['name', 'oeeLineId'],
      limit: 20,
      ...(search ? { search: { searchedFields: ['name'], searchText: search } } : {}),
      ...(boardId
        ? {
            sort: [
              { active: `id = ${boardId}`, direction: 'desc' },
              { active: 'id', direction: 'desc' },
            ],
          }
        : {}),
    });
  }

  protected toggleLineShiftFilter(): void {
    if (!this.useBoardOeeLineShifts()) {
      // The line shifts are still in the store, so no request is needed.
      this.useBoardOeeLineShifts.set(true);

      return;
    }

    this.store.loadShiftOptions({});

    // Swapping the source right away would show an empty list and resize the open panel.
    this.subscriptions.push(
      this.store
        .select((state) => state.shiftOptionsLoading)
        .pipe(
          filter((status: ELoadStatus) => status === ELoadStatus.success),
          take(1),
        )
        .subscribe((): void => this.useBoardOeeLineShifts.set(false)),
    );
  }

  public initializeLinkItem(): void {
    const event: Event = new Event('triggerClickNewLink');
    window.dispatchEvent(event);
    this.scrollIntoView();
  }

  private scrollIntoView(): void {
    setTimeout(() => {
      const element: Element | null = document.querySelector('#appLinkItem');

      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  public onMemoryStoredAttachmentsChange(memoryStoredAttachments: MemoryStoredItemAttachmentFile[]): void {
    this.pendingAttachmentFiles = memoryStoredAttachments;
    this.isMaxFilesCountExceeded = this.pendingAttachmentFiles.length >= 5;
  }

  public handleLinkedItems(items: IItemLinks[]): void {
    this.pendingLinkedItems = items;
  }

  public onFileChange(event: Event, inputElem?: HTMLInputElement): void {
    this.selectedFileChangeEvent = { event, inputElem };
  }

  public onPhotoCapture(file: File): void {
    this.capturedPhotoFile = file;
  }

  public onAttachmentRulesChange(rules: TAttachmentRules): void {
    this.attachmentRules = rules;
  }

  public onDeviceCameraAvailabilityChange(isAvailable: boolean): void {
    this.isDeviceCameraAvailable = isAvailable;
  }

  public onMaxFilesCountExceededChange(isExceeded: boolean): void {
    if (this.pendingAttachmentFiles.length >= 5) {
      this.isMaxFilesCountExceeded = isExceeded;
    }
  }

  public onAttachmentOngoingProcessChange(hasOngoingProcess: boolean): void {
    this.hasAttachmentComponentOngoingProcess = hasOngoingProcess;
  }

  public openCamera(): void {
    const dialogRef = this.dialog.open(DeviceCameraModalComponent, {
      height: '600px',
      width: '600px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.onPhotoCapture(result);
      }
    });
  }

  public openEscalateModal(): void {
    const dialogRef = this.dialog.open(ItemEscalateComponent, {
      data: this.item,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((closeModal: boolean | null) => {
      if (closeModal) {
        this.closeModal(false);
      }
    });
  }

  public openChoreography(): void {
    const itemKey: string | undefined = this.item?.key;

    if (!itemKey) {
      return;
    }

    const commands: string[] = ['dashboard', 'choreography', itemKey];
    const { ref } = this.dialogRef;

    // Iframe / standalone item page: there is no dialog to close, just navigate.
    if (!(ref instanceof MatDialogRef)) {
      this.router.navigate(commands);

      return;
    }

    const pathBeforeClose: string = this.location.path();
    // Navigate only after the dialog is gone: the board's own afterClosed handler (subscribed earlier, so it runs
    // first) pushes the board URL without the item key. Replace that entry so browser Back returns to the item.
    const navigationSubscription: Subscription = ref
      .afterClosed()
      .pipe(take(1))
      .subscribe(
        (): void => void this.router.navigate(commands, { replaceUrl: this.location.path() !== pathBeforeClose }),
      );

    if (this.addEditForm.isThereUnsavedChanges()) {
      this.doCancelConfirmation(() => navigationSubscription.unsubscribe());

      return;
    }

    this.dialogRef.close();
  }

  public openRecurrenceEditModalIfEnabled(): void {
    this.addEditForm.controls.cronEditor.controls.cron.reset(
      this.addEditForm.controls.cronEditor?.controls?.cron?.value ?? null,
    );

    this.dialog.open(RecurringItemConfigurationComponent, {
      data: { formGroup: this.addEditForm.controls.cronEditor, mode: this.item?.itemRecurrenceId ? 'show' : 'manage' },
      disableClose: true,
      maxWidth: '100%',
      restoreFocus: false,
      width: EDialogWidth.large,
    });
  }

  private getRecursivelyUpdatedFields(fieldsSet: readonly TItemUpdateFields[]): TItemUpdateFields[] {
    if (this.item === undefined) {
      return [];
    }

    const newValues = this.formatOnePayload(this.addEditForm.value as TFormItem, true) as TEditItem;

    return fieldsSet.filter((field) => {
      const oldValue = this.extractOldItemValue(field);

      return !isEqual(oldValue, newValues[field]);
    });
  }

  private extractOldItemValue(field: TItemUpdateFields): string | number | null | TItemCustomFieldValue[] {
    if (!this.item) {
      return null;
    }

    switch (field) {
      case 'boardItemConfigurationId':
        return this.item.boardItemConfiguration.id;
      case 'dueDate':
        return dayjs.tz(this.item.dueDate, 'UTC').format(API_DATE_FORMAT);
      default:
        return this.item[field];
    }
  }

  private takeSubmitAction(): void {
    switch (this.selectedRecurrenceEditType) {
      case EItemRecurrenceEditType.onlyThis:
        if (!this.item?.id) {
          this.store.addItemData(this.formatOnePayload(this.addEditForm.value as TFormItem) as TAddItem);

          return;
        }

        this.store.editItemData({
          dto: this.formatOnePayload(this.addEditForm.value as TFormItem, true) as TEditItem,
          id: this.item.id,
        });

        return;
      case EItemRecurrenceEditType.all:
      case EItemRecurrenceEditType.thisAndFollowing:
        if (!this.item?.itemRecurrence) {
          return;
        }

        this.recurringItemStore.update({
          fieldsToUpdate: pick(
            this.formatOnePayload(this.addEditForm.value as TFormItem, true) as TEditItem,
            this.getRecursivelyUpdatedFields(itemFieldsThatCanBeRecursivelyUpdated),
          ),
          itemRecurrenceId: this.item.itemRecurrence.id,
          startDate:
            this.selectedRecurrenceEditType === EItemRecurrenceEditType.all
              ? null
              : dayjs.tz(this.item.dueDate, 'UTC').format(API_DATE_FORMAT),
        });

        const changedFieldsThatCannotBeRecursivelyUpdated = this.getRecursivelyUpdatedFields(
          itemFieldsThatCannotBeRecursivelyUpdated,
        );

        if (changedFieldsThatCannotBeRecursivelyUpdated.length) {
          this.store.editItemData({
            dto: pick(
              this.formatOnePayload(this.addEditForm.value as TFormItem, true) as TEditItem,
              changedFieldsThatCannotBeRecursivelyUpdated,
            ),
            id: this.item.id,
          });
        }

        return;
    }
  }

  private formatOnePayload(item: TFormItem, isEdit = false): TAddItem | TEditItem {
    const values: TItemCustomFieldValue[] = this.itemsModalService.prepareValuesArray(
      this.fieldSetAssignments,
      item,
      this.item,
    );
    const canChangeBoard = !isEdit || (isEdit && !!this.item?.isQuick);
    const formControls = this.addEditForm.controls;

    return {
      assigneeId: item.assignee[0]?.id ?? null,
      reporterId: item.reporter?.[0]?.id ?? null,
      ...(canChangeBoard ? { boardId: item.boardId[0]?.id } : {}),
      ...(canChangeBoard ? { itemType: item.itemType[0]?.id } : {}),
      boardItemConfigurationId:
        item.itemCategory?.[0]?.id ??
        (
          find(this.store.boardItemConfigurations(), {
            boardId: null,
            itemType: formControls.itemType.value?.[0]?.id,
          }) as IBoardItemConfigurationBase
        )?.id,
      currentWorkflowStepId: item.status[0]?.id,
      description: this.getItemDescription(item.description),
      dueDate: this.dueDate?.startDate ? DateUtilitiesService.convertUserDateToUTCString(this.dueDate.startDate) : null,
      eventDate: item.eventDate?.startDate
        ? DateUtilitiesService.convertUserDateToUTCString(item.eventDate.startDate)
        : null,
      name: FormUtilitiesService.trimIfString(item.name),
      priority: item.priority?.[0]?.id ?? null,
      shiftId: item.shift.length ? (item.shift[0]?.id ?? null) : null,
      values,
      ...this.getRecurringItemConfigurationPayload(),
    };
  }

  private getRecurringItemConfigurationPayload(): {
    itemRecurrence?: Omit<IItemRecurrence, 'id'>;
  } {
    const { controls } = this.addEditForm;

    return !this.item?.itemRecurrenceId && controls.cronEditor.controls.cron.value !== null
      ? {
          itemRecurrence: {
            baseWorkflowStepId: controls.status.value![0].id,
            cron: controls.cronEditor.controls.cron.value,
            selectedLimitType: controls.cronEditor.controls.selectedLimitType.value!,
            ...(controls.cronEditor.controls.selectedLimitType.value === ERecursionLimitType.endDateLimit
              ? {
                  endDateLimit: DateUtilitiesService.convertUserDateToUTCString(
                    controls.cronEditor.controls.endDateLimit.value!.startDate!,
                  ),
                }
              : { numberLimit: controls.cronEditor.controls.numberLimit.value! }),
          },
        }
      : {};
  }

  private getItemDescription(description: string | Record<string, unknown>): string | null {
    if (typeof description === 'string') {
      return description;
    }

    if (typeof description === 'object' && description !== null) {
      return toHTML(description);
    }

    return null;
  }

  private initializeFormForEdit(): void {
    if (!this.item) {
      return;
    }

    this.dueDate = this.item.dueDate
      ? {
          endDate: DateUtilitiesService.convertUTCToUserTimezone(this.item.dueDate),
          startDate: DateUtilitiesService.convertUTCToUserTimezone(this.item.dueDate),
        }
      : undefined;

    this.addEditForm.reset({
      ...this.item,
      ...(this.item.assigneeId
        ? { assignee: [{ id: this.item.assigneeId!, name: this.item.assignee!.name }] }
        : { assignee: [] }),
      boardId: [{ id: this.item.boardId, name: this.item.board?.name ?? '...' }],
      ...(this.item.boardItemConfiguration.itemCategory
        ? { itemCategory: [{ id: this.item.boardItemConfigurationId, name: '...' }] as IItemCategoryOption[] }
        : { itemCategory: [] }),
      dueDate: this.dueDate,
      itemType: [{ id: this.item.boardItemConfiguration.itemType, name: '...' }],
      reporter: this.item.reporter ? [{ id: this.item.reporter.id, name: this.item.reporter.name }] : [],
      status: [
        {
          id: this.item.currentWorkflowStepId,
          name: this.item.currentWorkflowStep.isDefault
            ? this.translate.instant(`system.label.${this.item.currentWorkflowStep.name}`)
            : this.item.currentWorkflowStep.name,
        },
      ],
      ...(this.item.itemRecurrence
        ? {
            cronEditor: {
              cron: this.item.itemRecurrence.cron,
              endDateLimit: this.item.itemRecurrence.endDateLimit
                ? {
                    endDate: DateUtilitiesService.convertUTCToUserTimezone(this.item.itemRecurrence.endDateLimit),
                    startDate: DateUtilitiesService.convertUTCToUserTimezone(this.item.itemRecurrence.endDateLimit),
                  }
                : undefined,
              numberLimit: this.item.itemRecurrence.numberLimit,
              selectedLimitType: this.item.itemRecurrence.selectedLimitType,
            },
          }
        : {}),
      eventDate: this.item.eventDate
        ? {
            endDate: DateUtilitiesService.convertUTCToUserTimezone(this.item.eventDate),
            startDate: DateUtilitiesService.convertUTCToUserTimezone(this.item.eventDate),
          }
        : undefined,
      priority: this.item.priority ? [this.itemPriorityService.getOption(this.item.priority)] : [],
      shift: this.item.shiftId ? [{ id: this.item.shiftId, name: '...' }] : [],
    });

    if (!this.item.isQuick) {
      this.addEditForm.get('boardId')?.disable();
      this.addEditForm.get('itemType')?.disable();
    }
  }

  private updateStatusOptions(): void {
    if (
      !this.addEditForm.controls.boardId.getRawValue()?.length ||
      !this.addEditForm.controls.itemType.getRawValue()?.length
    ) {
      return;
    }

    let modifiedBoardItemConfigurationId: number | undefined =
      this.addEditForm.value.itemCategory?.[0]?.id ?? this.item?.boardItemConfigurationId;

    if (!modifiedBoardItemConfigurationId) {
      modifiedBoardItemConfigurationId = this.store
        .boardItemConfigurations()
        .filter(
          (boardItemConfiguration) =>
            !boardItemConfiguration.boardId &&
            !boardItemConfiguration.itemCategory &&
            boardItemConfiguration.itemType === this.addEditForm.controls.itemType.getRawValue()?.[0]?.id,
        )?.[0]?.id;
    }

    const workflow: TWorkflow | undefined = find(this.store.boardItemConfigurations(), {
      id: modifiedBoardItemConfigurationId,
    })?.workflow;

    if (!workflow) {
      return;
    }

    this.currentWorkflow = workflow;

    const currentStepId: number | undefined = this.item?.currentWorkflowStepId;
    const currentStepInWorkflow: boolean =
      currentStepId !== undefined &&
      workflow.workflowStepAssignments.some(
        (assignment: TWorkflowStepAssignment) =>
          assignment.workflowStep.id === currentStepId || assignment.nextWorkflowStep?.id === currentStepId,
      );

    this.workflowStepOptions = this.itemsModalService.prepareWorkflowStepOptions(
      workflow.workflowStepAssignments,
      currentStepInWorkflow ? currentStepId : undefined,
    );

    const isCreate: boolean = !currentStepId && !this.data.idForEdit;

    if ((isCreate || !currentStepInWorkflow) && workflow.startWorkflowStepId) {
      this.workflowStepOptions = this.workflowStepOptions.filter(
        (option: IDropdownOption) => option.id === workflow.startWorkflowStepId,
      );
    }

    if (currentStepId !== undefined && !currentStepInWorkflow) {
      const startOption: IDropdownOption | undefined = this.workflowStepOptions.find(
        (option: IDropdownOption) => option.id === workflow.startWorkflowStepId,
      );

      if (startOption) {
        this.addEditForm.controls.status.reset([startOption]);
      }
    }

    this.setDefaultStatusForAddItem(workflow.workflowStepAssignments, workflow.startWorkflowStepId);
  }

  public openWorkflowDiagram(): void {
    if (!this.currentWorkflow) {
      return;
    }

    const assignments: TWorkflowStepAssignment[] = this.currentWorkflow.workflowStepAssignments ?? [];
    const stepsById = new Map<number, IWorkflowStepOption>();
    const addStep = (step?: IWorkflowStepBase | null): void => {
      if (step) {
        stepsById.set(step.id, {
          id: step.id,
          isDefault: step.isDefault,
          name: step.name,
          statusCategory: step.statusCategory,
        });
      }
    };

    for (const assignment of assignments) {
      addStep(assignment.workflowStep);
      addStep(assignment.nextWorkflowStep);
    }

    const rawName: string = this.currentWorkflow.name ?? '';
    const data: IWorkflowDiagramData = {
      name: rawName === DEFAULT_WORKFLOW_NAME ? this.translate.instant('field.defaultWorkflow') : rawName,
      startWorkflowStepId: this.currentWorkflow.startWorkflowStepId ?? null,
      steps: [...stepsById.values()],
      transitions: assignments
        .filter((assignment: TWorkflowStepAssignment) => assignment.workflowStep && assignment.nextWorkflowStep)
        .map((assignment: TWorkflowStepAssignment) => ({
          nextWorkflowStepId: assignment.nextWorkflowStep!.id,
          workflowStepId: assignment.workflowStep.id,
        })),
    };

    this.dialog.open(WorkflowDiagramDialogComponent, {
      data,
      maxWidth: '100%',
      restoreFocus: false,
      width: EDialogWidth.medium,
    });
  }

  private setDefaultStatusForAddItem(
    workflowStepAssignments: TWorkflowStepAssignment[],
    startWorkflowStepId?: number | null,
  ): void {
    if (!this.addEditForm.controls.status.value?.length && !this.data.idForEdit) {
      const workflowStepOptionsWithCategory: IDropdownOption[] = this.itemsModalService.prepareWorkflowStepOptions(
        workflowStepAssignments,
        this.item?.currentWorkflowStepId,
        true,
      );

      const startStep: IDropdownOption | undefined = startWorkflowStepId
        ? (find(workflowStepOptionsWithCategory, { id: startWorkflowStepId }) as IDropdownOption | undefined)
        : undefined;

      const defaultStep: IDropdownOption | undefined =
        startStep ?? (find(workflowStepOptionsWithCategory, { statusCategory: 'todo' }) as IDropdownOption | undefined);

      if (defaultStep) {
        this.addEditForm.controls.status.reset([defaultStep]);
      }
    }
  }

  private createSubscriptions(): void {
    const initiallySelectedBoardId: number | undefined =
      this.addEditForm.controls.boardId.value?.[0]?.id ?? this.data.selectedBoard?.id;
    const boardIdChanges = this.addEditForm.controls.boardId.valueChanges.pipe(
      distinctUntilChanged((previous, current) => previous?.[0]?.id === current?.[0]?.id),
      map((board) => get(board, '0.id')),
    );
    const itemTypeChanges = this.addEditForm.controls.itemType.valueChanges.pipe(
      distinctUntilChanged((previous, current) => previous?.[0]?.id === current?.[0]?.id),
      map((itemType) => get(itemType, '0.id')),
    );
    const itemCategoryChanges = this.addEditForm.controls.itemCategory.valueChanges.pipe(
      distinctUntilChanged((previous, current) => previous?.[0]?.id === current?.[0]?.id),
      map((boardItemConfiguration) => get(boardItemConfiguration, '0.id')),
    );

    this.subscriptions.push(
      this.getBoardIdChangesSubscription(boardIdChanges, initiallySelectedBoardId),
      this.getOeeLineShiftsSubscription(boardIdChanges, initiallySelectedBoardId),
      this.getBoardAndTypeChangesSubscription(boardIdChanges, itemTypeChanges, initiallySelectedBoardId),
      this.getItemCategoryChangesSubscription(itemCategoryChanges),
      this.getBoardItemConfigurationsLoadedSubscription(),
      this.getInitializeFormForEditSubscription(),
      this.getDefaultFieldValidatorSubscription(),
      this.getItemsLinkedSubscription(),
      this.getRecommendedBoardSubscription(),
      this.getDueDateEnableDisableSubscription(),
      this.closeModalAfterUpdateCompletedSubscription(),
    );
  }

  private closeModalAfterUpdateCompletedSubscription(): Subscription {
    return merge(
      this.store
        .select((state) => state.editLoadStatus)
        .pipe(
          filter((status) => status === ELoadStatus.success),
          map(() => null),
        ),
      this.store.select((state) => state.createdItem).pipe(filter(Boolean)),
      this.recurringItemStore
        .select((state) => state.updateLoadStatus)
        .pipe(
          filter((status) => status === ELoadStatus.success),
          map(() => null),
        ),
    )
      .pipe(
        switchMap((createdItem) => {
          const criteriaBeforeModalClose: Observable<unknown>[] = [];

          if (!createdItem) {
            return new Observable().pipe(startWith(null));
          }

          if (this.pendingLinkedItems.length > 0) {
            const requestParams: IAddItemLink[] = this.pendingLinkedItems.map((link: IItemLinks) => ({
              sourceItemBoardId: Number(createdItem?.boardId),
              sourceItemId: Number(createdItem?.id),
              startDateForRecurringUpdate: null,
              targetItemBoardId: link.targetItemBoardId,
              targetItemId: link.targetItemId,
            }));

            this.linkItemStore.addManyItemRelationshipData(requestParams);
            criteriaBeforeModalClose.push(
              this.linkItemStore
                .select((state) => state.crudLoading)
                .pipe(filter((status) => status === ELoadStatus.success)),
            );
          }

          if (this.pendingAttachmentFiles.length) {
            this.itemsAttachmentsStore.addAttachments({
              boardId: createdItem.boardId,
              files: this.pendingAttachmentFiles.map((attachment) => attachment.file),
              itemId: createdItem.id,
              startDateForRecurringUpdate: null,
            });
            criteriaBeforeModalClose.push(
              this.itemsAttachmentsStore
                .select((state) => state.addAttachmentsLoading)
                .pipe(filter((status) => status === ELoadStatus.success)),
            );
          }

          return criteriaBeforeModalClose.length
            ? combineLatest(criteriaBeforeModalClose)
            : new Observable().pipe(startWith(null));
        }),
      )
      .subscribe(() => {
        this.closeModal();
      });
  }

  private getDueDateEnableDisableSubscription(): Subscription {
    return this.addEditForm.controls.cronEditor.controls.cron.valueChanges
      .pipe(startWith(this.addEditForm.controls.cronEditor.controls.cron.value))
      .subscribe((cronExpression) => {
        if (cronExpression !== null && !this.item?.itemRecurrenceId) {
          this.addEditForm.controls.dueDate.reset(null);
          this.addEditForm.controls.dueDate.disable();
        } else {
          this.addEditForm.controls.dueDate.enable();
        }
      });
  }

  private getBoardAndTypeChangesSubscription(
    boardIdChanges: Observable<undefined | number>,
    itemTypeChanges: Observable<undefined | string>,
    initiallySelectedBoardId?: number,
  ): Subscription {
    return combineLatest([boardIdChanges, itemTypeChanges])
      .pipe(startWith([initiallySelectedBoardId, this.defaultItemType.id]))
      .subscribe(([boardId, itemTypeId]) => {
        if (!boardId || !itemTypeId) {
          this.addEditForm.get('itemCategory')?.reset([]);
          this.addEditForm.get('status')?.reset(null);
          this.workflowStepOptions = [];

          return;
        }

        this.removedUnusedCustomFieldControls();

        this.store.loadBoardItemConfigurations({
          filters: [
            { field: 'boardId', ids: [boardId] as number[] },
            { field: 'itemType', ids: [itemTypeId] as string[] },
          ],
          join: [
            'itemCategory||name,isDefault,isActive',
            'workflow||id,name,startWorkflowStepId',
            'workflow.workflowStepAssignments||id',
            'workflow.workflowStepAssignments.workflowStep||name,isDefault,statusCategory',
            'workflow.workflowStepAssignments.nextWorkflowStep||name,isDefault,statusCategory',
            'fieldSet||isDefault',
            'fieldSet.fieldSetAssignments||fieldId,fieldOrder,isRequired',
            'fieldSet.fieldSetAssignments.field||name,type,options,externalDropdownId,isActive,isDefault',
          ],
          orFilters: [{ field: 'boardId', ids: [], operator: '$isnull' }],
        });
      });
  }

  private getItemCategoryChangesSubscription(itemCategoryChanges: Observable<undefined | number>): Subscription {
    return itemCategoryChanges.subscribe(() => {
      this.addEditForm.get('status')?.setValue(null);
      this.updateStatusOptions();

      if (this.store.boardItemConfigurations().length && this.addEditForm.value.itemCategory?.[0]?.id) {
        this.setFieldSetAssignments(this.store.boardItemConfigurations());
      } else {
        this.fieldSetAssignments = [];
      }

      this.removedUnusedCustomFieldControls();
    });
  }

  private setFieldSetAssignments(boardItemConfigurations: TBoardItemConfigurationWithWorkflow[]): void {
    const boardItemConfiguration: TBoardItemConfigurationWithWorkflow | undefined = find(boardItemConfigurations, {
      id: this.addEditForm.value.itemCategory?.[0]?.id,
    });

    if (!boardItemConfiguration || !boardItemConfiguration.fieldSet) {
      this.fieldSetAssignments = [];

      return;
    }

    this.isDefaultFieldSetSelected = boardItemConfiguration.fieldSet.isDefault;
    this.fieldSetAssignments = boardItemConfiguration.fieldSet.fieldSetAssignments;
  }

  private getOeeLineShiftsSubscription(
    boardIdChanges: Observable<undefined | number>,
    initiallySelectedBoardId?: number,
  ): Subscription {
    return combineLatest([
      boardIdChanges.pipe(startWith(initiallySelectedBoardId)),
      this.store.select((state) => state.boardOptions),
      this.store.select((state) => state.dataForEdit),
    ])
      .pipe(
        // The board has to be loaded first, it decides which list the shifts come from.
        map(([boardId, boardOptions, dataForEdit]): IBoard | undefined => {
          if (!boardId) {
            return undefined;
          }

          return dataForEdit?.boardId === boardId
            ? dataForEdit.board
            : boardOptions.find((board: IBoard) => board.id === boardId);
        }),
        filter(Boolean),
        map((board: IBoard): number | null => board.oeeLineId ?? null),
        distinctUntilChanged(),
        // The first value is the board the modal opened with, not a switch by the user.
        map((oeeLineId: number | null, index: number) => ({ isBoardSwitch: index > 0, oeeLineId })),
      )
      .subscribe(({ isBoardSwitch, oeeLineId }): void => {
        this.boardOeeLineId.set(oeeLineId);
        this.useBoardOeeLineShifts.set(Boolean(oeeLineId));

        if (isBoardSwitch) {
          // A board on a line fills the field again once its current shift arrives.
          this.addEditForm.controls.shift.reset([]);
        }

        if (!oeeLineId) {
          this.store.loadShiftOptions({});

          return;
        }

        this.oeeTrackerStore.loadLineShifts({ lineId: oeeLineId, withCurrentShift: !this.data.idForEdit });
      });
  }

  private getBoardIdChangesSubscription(
    boardIdChanges: Observable<undefined | number>,
    initiallySelectedBoardId?: number,
  ): Subscription {
    return boardIdChanges.pipe(startWith(initiallySelectedBoardId)).subscribe((boardId?: number): void => {
      this.selectedBoardId = boardId;

      if (!this.selectedBoardId) {
        return;
      }

      this.searchBoardMembers('', this.item?.assigneeId);
      this.searchReporterMembers('', this.addEditForm.controls.reporter.value?.[0]?.id ?? this.item?.reporterId);

      if (this.addEditForm.controls.itemType.value?.length) {
        this.addEditForm.controls.itemType.updateValueAndValidity();
      }
    });
  }

  private getInitializeFormForEditSubscription(): Subscription {
    return this.store
      .select((state) => state.dataForEdit)
      .pipe(filter(Boolean))
      .subscribe((dataForEdit: IItemJoins) => {
        this.item = dataForEdit;
        this.data.title = dataForEdit.key;
        this.currentWorkflow = this.item.boardItemConfiguration?.workflow ?? null;
        this.workflowStepOptions = this.itemsModalService.prepareWorkflowStepOptions(
          this.item.boardItemConfiguration.workflow.workflowStepAssignments,
          this.item.currentWorkflowStepId,
        );
        this.initializeFormForEdit();
      });
  }

  private getDefaultFieldValidatorSubscription(): Subscription {
    return this.store
      .select((state) => state.defaultFieldSet)
      .pipe(filter(Boolean))
      .subscribe((defaultFieldSet: TFieldSet) => {
        for (const fieldSetAssignment of defaultFieldSet.fieldSetAssignments) {
          const formControl: FormControl | null = this.addEditForm.get(
            fieldSetAssignment.field.name,
          ) as FormControl | null;

          if (!formControl) {
            continue;
          }

          if (fieldSetAssignment.isRequired) {
            formControl.addValidators([Validators.required]);

            if (fieldSetAssignment.field.name === 'description') {
              formControl.addValidators([TextEditorRequiredValidator()]);
            }
          } else {
            formControl.removeValidators([Validators.required]);
          }

          formControl.updateValueAndValidity();
        }
      });
  }

  private getBoardItemConfigurationsLoadedSubscription(): Subscription {
    return this.store
      .select((state) => state.boardItemConfigurations)
      .pipe(filter(Boolean))
      .subscribe((boardItemConfigurations: TBoardItemConfigurationWithWorkflow[]) => {
        const boardConfigurationId: number | undefined = this.addEditForm.get('itemCategory')?.value?.[0]?.id;

        if (!boardConfigurationId || !boardItemConfigurations.length) {
          this.addEditForm.get('itemCategory')?.reset([]);

          return;
        }

        this.selectCommonItemCategory(boardItemConfigurations);
        this.setFieldSetAssignments(boardItemConfigurations);
      });
  }

  private selectCommonItemCategory(boardItemConfigurations: TBoardItemConfigurationWithWorkflow[]): void {
    const option: IItemCategoryOption | undefined = get(this.addEditForm.controls.itemCategory.value, '0');
    const commonItemCategoryConfigurationId = find(boardItemConfigurations, {
      itemCategory: { id: option?.itemCategoryId },
      itemType: this.addEditForm.controls.itemType.value?.[0]?.id,
    })?.id;

    if (!commonItemCategoryConfigurationId && option?.itemCategoryId) {
      this.addEditForm.get('itemCategory')?.reset([]);
    } else if (commonItemCategoryConfigurationId) {
      this.addEditForm.controls.itemCategory.reset(
        this.store.itemCategoryOptions().filter(({ id }) => id === commonItemCategoryConfigurationId),
      );
    }
  }

  private getItemsLinkedSubscription(): Subscription {
    return this.itemsService.itemsLinked$?.subscribe(() => {
      this.closeModal();
    });
  }

  private getRecommendedBoardSubscription(): Subscription {
    return this.store.recommendedBoard$.subscribe((recommendedBoard): void => {
      if (!recommendedBoard?.boardId || !recommendedBoard?.boardName) {
        return;
      }

      this.addEditForm.controls.boardId.reset([{ id: recommendedBoard.boardId, name: recommendedBoard.boardName }]);
      this.addEditForm.controls.itemType.updateValueAndValidity();
    });
  }

  private closeModal(notifyParentIframe = true): void {
    this.store
      .select((state) => state.createdItem)
      .pipe(take(1))
      .subscribe((createdItem) => {
        const activeItem: IItemBase<dayjs.Dayjs> | undefined = createdItem || this.item;
        const isEditMode = Boolean(this.item?.id || this.data?.idForEdit);

        if (isEditMode) {
          this.snackbarService.open(this.translate.instant('system.message.changesSuccessfully'), ESnackbar.success);
        } else if (activeItem?.id && activeItem?.key) {
          const snackBarRef: MatSnackBarRef<TextOnlySnackBar> = this.snackbarService.open(
            this.translate.instant('system.message.itemCreatedSuccessfully', { itemId: activeItem?.key }),
            ESnackbar.success,
            15000,
            this.translate.instant('button.view'),
          );

          snackBarRef
            .onAction()
            .pipe(take(1))
            .subscribe(() => {
              this.openItemEditModal(activeItem?.id);
            });
        }

        if (notifyParentIframe) {
          this.sendIframeDialogClosedMessage(activeItem?.key);
        }
      });

    this.dialogRef.close(true);
    this.itemsService.triggerRefresh();
  }

  private getDefaultFieldSet(): void {
    this.store.loadDefaultFieldSet({
      fields: ['name', 'isDefault'],
      filters: [{ field: 'isDefault', ids: [true] }],
      join: ['fieldSetAssignments||fieldOrder,fieldId,isRequired', 'fieldSetAssignments.field||name,type,isDefault'],
    });
  }

  public onClickCommentButton(): void {
    this.isShowComments = true;
  }

  public onClickHistoryButton(): void {
    this.isShowComments = false;
  }

  public getUnloadPreventionModalData = (): IConfirmationDialogConfig => {
    return this.addEditForm.valid
      ? {
          cancelButtonText: this.translate.instant('button.cancel'),
          content: this.translate.instant('page.items.discardUnsavedChangesConfirmation.contentIfFormIsValid'),
          dontSubmitButtonText: this.translate.instant('button.dontApply'),
          submitButtonText: this.translate.instant('button.apply'),
          title: this.translate.instant('field.unsavedChanges'),
        }
      : {
          cancelButtonText: this.translate.instant('button.no'),
          content: this.translate.instant('page.items.discardUnsavedChangesConfirmation.contentIfFormIsInvalid'),
          submitButtonText: this.translate.instant('button.yes'),
          title: this.translate.instant('field.unsavedChanges'),
        };
  };

  public onDismiss(): void {
    if (this.addEditForm.isThereUnsavedChanges()) {
      this.doCancelConfirmation();
    } else {
      this.dialogRef.close();
    }
  }

  public doCancelConfirmation(onCancel?: () => void): void {
    this.removedUnusedCustomFieldControls();
    const modalData = this.getUnloadPreventionModalData();

    this.subscriptions.push(
      this.dialog
        .open(ConfirmationDialogComponent, {
          data: modalData,
        })
        .afterClosed()
        .subscribe((isConfirmed) => {
          if (isConfirmed) {
            if (this.addEditForm.valid) {
              this.onFormDialogSubmit();
            } else {
              this.dialogRef.close();
            }
          } else if (isConfirmed === false) {
            this.dialogRef.close();
          } else {
            onCancel?.();
          }
        }),
    );
  }

  private removedUnusedCustomFieldControls(): void {
    const currentFieldNames: string[] = this.fieldSetAssignments?.map((fieldSetAssignment: TFieldSetAssignment) => {
      return ItemCustomFieldsService.generateCustomFieldControlName(
        fieldSetAssignment.field.name,
        fieldSetAssignment.field.id,
      );
    });
    const customFieldsFormNames: string[] = Object.keys(this.addEditForm.controls.customFields.controls);
    const unnecessaryFieldControlNames: string[] = difference(customFieldsFormNames, currentFieldNames);

    for (const unnecessaryFieldControlName of unnecessaryFieldControlNames) {
      this.addEditForm.controls.customFields.removeControl(unnecessaryFieldControlName);
    }
  }

  private isQuickItemFilledByUser(): boolean {
    return Boolean(
      this.data.quickItem?.name?.trim() ||
        this.data.quickItem?.description?.trim() ||
        this.data.quickItem?.assignee?.length ||
        this.data.quickItemAttachments?.length,
    );
  }

  private fillReporterFieldForAdd(): void {
    if (!this.data.idForEdit) {
      this.addEditForm.controls.reporter.reset([{ id: UserEffects.getUser().id, name: UserEffects.getUser().name }]);
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
    this.editor.destroy();
    this.selectedFileChangeEvent = undefined;
  }

  public openWorkInstructionList(): void {
    if (!this.item?.id || !this.item?.boardId) {
      return;
    }

    const data = {
      boardId: this.item.boardId,
      dueDateForRecurrence: this.item.itemRecurrence ? this.item.dueDate : null,
      itemId: this.item.id,
    };

    this.dialog
      .open(ItemWorkInstructionListComponent, {
        data,
        disableClose: true,
        restoreFocus: false,
        width: EDialogWidth.large,
      })
      .afterClosed()
      .subscribe(() => {
        if (this.selectedBoardId && this.item?.id) {
          this.itemsService.triggerRefresh();
          this.triggerWorkInstructionLoad.emit();
        }
      });
  }

  private insertFieldsFromUrl(): void {
    for (const field of itemFieldsFromUrl) {
      if (this.fieldsFromUrl[field] === undefined) {
        continue;
      }

      switch (field) {
        case 'name':
          this.addEditForm.controls[field].reset(this.fieldsFromUrl[field]);
          break;
        case 'assignee':
        case 'boardId':
          this.addEditForm.controls[field].reset([{ id: this.fieldsFromUrl[field], name: '...' }]);
          break;
        case 'itemType':
          this.addEditForm.controls[field].reset([{ id: this.fieldsFromUrl[field], name: '...' }]);
          break;
        case 'itemCategory':
          break; /** Item category is handled separately in {@link insertItemCategoryFromUrl} */
      }
    }
  }

  private getFieldsFromUrl(): Partial<IItemFieldsFromUrl> {
    try {
      const raw = this.router.routerState.snapshot.root.queryParamMap.get('fields');

      return raw ? pick(JSON.parse(raw), itemFieldsFromUrl) : {};
    } catch (_) {
      return {};
    }
  }

  private insertItemCategoryFromUrl(): void {
    const itemCategoryFromUrl = this.fieldsFromUrl.itemCategory;

    if (!itemCategoryFromUrl) {
      return;
    }

    this.store
      .select((state) => state.boardItemConfigurations)
      .pipe(
        filter((configs) => {
          if (!configs || configs.length === 0) {
            return false;
          }

          const matchingOption: IItemCategoryOption | undefined = this.store
            .itemCategoryOptions()
            .find(({ itemCategoryId }) => itemCategoryId === itemCategoryFromUrl);

          return !!matchingOption;
        }),
        take(1),
      )
      .subscribe(() => {
        const matchingOption = this.store
          .itemCategoryOptions()
          .find(({ itemCategoryId }) => itemCategoryId === itemCategoryFromUrl);

        if (matchingOption) {
          this.addEditForm.controls.itemCategory.reset([matchingOption]);
        }
      });
  }

  private sendIframeDialogClosedMessage(key?: string): void {
    if (!this.isIframeRootDialog()) {
      return;
    }

    window.parent.postMessage(
      {
        payload: { key },
        type: 'DIALOG_CLOSED',
      },
      '*',
    );
  }

  private isIframeRootDialog(): boolean {
    return this.isIframe && this.dialogRef.ref.name === 'iframe';
  }

  private openItemEditModal(itemId: number): void {
    this.dialog.open(ItemsModalComponent, {
      ...ItemsModalComponent.modalParameters,
      data: {
        idForEdit: itemId,
        submitButtonText: this.translate.instant('button.save'),
      },
      restoreFocus: false,
    });
  }
}
